import { useCallback, useEffect, useRef } from "react";

import { NodeViewWrapper, useEditorState } from "@tiptap/react";
import { observer } from "mobx-react-lite";

import { nodeNestedHover } from "@/components/expression/utils/nodeNestedHover";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { WrapperNode } from "@/components/expression/WrapperNode";
import {
  findIndentOperator,
  INDENT_NODE_DATA_ATTRIBUTE,
  isMathIndentationDisabled,
} from "@/components/tiptap/utils/indentation";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { getIsFirefox } from "@/utils/getIsFirefox";
import { getIsWebkit } from "@/utils/getIsWebkit";
import { twMerge } from "@/utils/twMerge";
import { useResizeObserver } from "@/utils/useResizeObserver";

import { useNotifyNodesChanged } from "./expressionNodesChanged";
import { useAvailableWidth } from "./useAvailableWidth";

import type { ExpressionNodeExtensionOptions } from "./types";
import type { InlineNodeAttrs } from "@corca/tiptap";
import type { NodeViewProps } from "@tiptap/react";

export const ExpressionNodeRenderer = observer(function ExpressionNodeRenderer({
  node,
  editor,
  extension,
  getPos,
}: NodeViewProps) {
  const attrs = node.attrs as InlineNodeAttrs;
  const { nodeId, indentation, automaticIndentation } = attrs;
  const { doc } = extension.options as ExpressionNodeExtensionOptions;
  const expressionNode = doc?.nodes.get(nodeId);
  const ref = useRef<HTMLDivElement>(null);

  const availableWidth = useAvailableWidth(editor, getPos);
  const isCursored = !!(expressionNode && doc?.cursor.isOnNode(expressionNode));
  const isHovered = nodeNestedHover(expressionNode);
  const isInteracting = isCursored || isHovered;
  const pendingRectRef = useRef<{ width: number; height: number } | null>(null);

  const { cursorSide, hasContentAfter, hasContentBefore } = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const pos = getPos();
      const { from, to, $head } = e.state.selection;

      if (pos === undefined) {
        return { cursorSide: undefined, hasContentBefore: false, hasContentAfter: false };
      }

      const $pos = e.state.doc.resolve(pos);
      const { nodeAfter } = e.state.doc.resolve(pos + node.nodeSize);

      const hasContentBefore =
        $pos.nodeBefore !== null && $pos.nodeBefore.type.name !== "hardBreak";
      const hasContentAfter = nodeAfter !== null && nodeAfter.type.name !== "hardBreak";

      if (from !== to) {
        return { cursorSide: undefined, hasContentBefore, hasContentAfter };
      }

      let cursorSide: "before" | "after" | undefined = undefined;
      if (pos === $head.pos) {
        cursorSide = "before";
      } else if ($head.nodeBefore && pos === $head.pos - $head.nodeBefore.nodeSize) {
        cursorSide = "after";
      }

      return { cursorSide, hasContentBefore, hasContentAfter };
    },
    equalityFn: (a, b) => {
      return (
        !!b &&
        a.cursorSide === b.cursorSide &&
        a.hasContentBefore === b.hasContentBefore &&
        a.hasContentAfter === b.hasContentAfter
      );
    },
  });

  useNotifyNodesChanged(editor, getPos, expressionNode);

  const measureAndUpdate = useCallback(
    (entry: ResizeObserverEntry) => {
      const pos = getPos();
      if (pos === undefined) return;

      const el = entry.target as HTMLElement;
      const { width, height } = entry.contentRect;
      const {
        width: oldWidth,
        height: oldHeight,
        verticalOffset: oldVerticalOffset,
        indentNodeLeftOffset: oldLeftOffset = null,
        indentNodeRightOffset: oldRightOffset = null,
      } = attrs;

      const verticalOffset = measureVerticalOffset(el);

      // Compare floored values to avoid sub-pixel oscillation
      const sizeChanged =
        Math.floor(width) !== Math.floor(oldWidth ?? 0) ||
        Math.floor(height) !== Math.floor(oldHeight ?? 0) ||
        Math.floor(verticalOffset) !== Math.floor(oldVerticalOffset ?? 0);

      let nextLeftOffset: number | null = null;
      let nextRightOffset: number | null = null;

      if (isMathIndentationDisabled()) {
        // Offsets measured in this narrow layout would be wrong for wide clients, and old
        // ones no longer match the new size; cleared offsets make them measure their own DOM.
        if (!sizeChanged) return;
      } else {
        const indentOperator = findIndentOperator(doc, nodeId);
        const offsets = indentOperator ? measureIndentNodeOffsets(indentOperator.id, el) : null;
        nextLeftOffset = offsets?.left ?? null;
        nextRightOffset = offsets?.right ?? null;

        const offsetsChanged =
          nextLeftOffset !== oldLeftOffset || nextRightOffset !== oldRightOffset;

        if (!sizeChanged && !offsetsChanged) return;
      }

      editor.commands.updateInlineExpression(pos, {
        width,
        height,
        verticalOffset,
        indentNodeLeftOffset: nextLeftOffset,
        indentNodeRightOffset: nextRightOffset,
      });
    },
    [editor, getPos, attrs, doc, nodeId],
  );

  useResizeObserver(ref, (entry) => {
    if (isInteracting) {
      // Store the measurement — don't update Yjs while cursor or hover is active
      pendingRectRef.current = {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      };
      return;
    }

    // Got a fresh measurement — discard any stale pending rect
    pendingRectRef.current = null;
    measureAndUpdate(entry);
  });

  // When cursor/hover leaves, measure the element's actual dimensions using a
  // one-shot ResizeObserver. This avoids racing with the main resize observer —
  // observing an element immediately fires with its current contentRect.
  useEffect(() => {
    if (!isInteracting && pendingRectRef.current) {
      const el = ref.current;
      if (!el) {
        pendingRectRef.current = null;
        return;
      }

      const resizeObserver = new ResizeObserver((entries) => {
        resizeObserver.disconnect();
        pendingRectRef.current = null;
        const entry = entries[0];
        if (entry) {
          measureAndUpdate(entry);
        }
      });
      resizeObserver.observe(el);

      return () => resizeObserver.disconnect();
    }
  }, [isInteracting, measureAndUpdate]);

  // In dev mode, ReactNodeViewRenderer renders component twice, but getPos() returns position only in one of those renders.
  if (!expressionNode || !expressionNode.is(WrapperNodeModel) || getPos() === undefined) {
    return null;
  }

  const isFocused =
    !!cursorSide || (expressionNode.inlineNode && doc.cursor.isOnNode(expressionNode.inlineNode));

  return (
    <NodeViewWrapper
      as="span"
      // Browser page translation (Chrome iOS) rewrites text nodes; keep it out of formulas.
      translate="no"
      data-math=""
      className={twMerge(
        "relative transition-[margin] duration-100 mx-0 sm:left-0! indent-0",
        cursorSide === "after" && "mr-[2px]",
        cursorSide === "before" && "ml-[2px]",
      )}
      style={indentation ? { left: `${indentation}px` } : undefined}
    >
      {/*
        This span fixes selection issue in Firefox. It is important that contentEditable is `true` here.

        To reproduce:
        - write "(x) plain text" (where (x) is an expression at the start of the line)
        - select content from end to start (using Shift+Home)

        Without it, FF is unable to select content up to start of the line and also creates multiple selection ranges.
      */}
      {getIsFirefox() && (
        <span
          tabIndex={-1}
          aria-hidden
          contentEditable
          className={twMerge(
            "before:content-zero-space",
            !hasContentBefore && cursorSide === "before" && "inline-block w-0.5",
          )}
        />
      )}
      <NodeStyleProvider availableWidth={availableWidth}>
        <WrapperNode
          ref={ref}
          node={expressionNode}
          enableIndentation={!!automaticIndentation}
          isFocused={isFocused}
          className="not-italic text-document-text font-mono inline-flex"
        />
      </NodeStyleProvider>
      {!getIsFirefox() && !getIsWebkit() && !hasContentAfter && cursorSide === "after" && (
        <span aria-hidden className="inline-block w-0.5" />
      )}
    </NodeViewWrapper>
  );
});

/**
 * How far the expression's top sits above the middle of the text line around it;
 * the lazy placeholder uses it to align on the same baseline. Both rects are
 * measured in viewport space — the wrapper is the expression's offsetParent, so
 * mixing `offsetTop`s would add the line's position within its block.
 */
function measureVerticalOffset(el: HTMLElement): number {
  const parent = el.parentElement;
  if (!parent) return 0;
  const parentRect = parent.getBoundingClientRect();
  return parentRect.top + parentRect.height / 2 - el.getBoundingClientRect().top;
}

function measureIndentNodeOffsets(operatorId?: string, container?: HTMLElement) {
  const operatorEl = container?.querySelector?.(`[${INDENT_NODE_DATA_ATTRIBUTE}="${operatorId}"]`);

  if (!container || !operatorEl) {
    return null;
  }

  const expressionRect = container.getBoundingClientRect();
  const operatorRect = operatorEl.getBoundingClientRect();

  return {
    left: Math.round(operatorRect.left - expressionRect.left),
    right: Math.round(expressionRect.right - operatorRect.right),
  };
}
